// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, Search, ArrowLeft, Loader2, Calendar, Home, Phone, Download, Copy
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { exportAnalysisToPDF, copyToClipboard, formatAnalysisAsText } from "@/lib/pdfExport";
import { useSEO } from "@/hooks/useSEO";

interface Analysis {
  id: string;
  file_name: string;
  summary: string;
  keywords: string[];
  sentiment: { 
    overall: string;
    positive?: number;
    neutral?: number;
    negative?: number;
  };
  themes?: Array<{ theme: string; sentiment: string }>;
  language?: string;
  created_at: string;
}

export default function KompaktHistory() {
  // SEO für Kompakt History
  useSEO({
    title: 'Analyse-Verlauf | Klartech Kompakt',
    description: 'Ihre gespeicherten Dokumentenanalysen im Überblick. Greifen Sie auf frühere KI-Analysen zu.',
    canonical: 'https://klartech-mueritz.de/kompakt/history',
    noIndex: true, // Persönliche Daten, nicht indexieren
  });
  
  const [apiKey] = useState<string | null>(localStorage.getItem('klartech_kompakt_api_key'));
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedAnalysis, setSelectedAnalysis] = useState<Analysis | null>(null);

  useEffect(() => {
    if (apiKey) {
      fetchHistory();
    } else {
      setIsLoading(false);
    }
  }, [apiKey]);

  const fetchHistory = async (searchTerm?: string) => {
    if (!apiKey) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('kompakt-history', {
        body: { apiKey, search: searchTerm, limit: 100 }
      });

      if (error) throw error;
      setAnalyses(data.analyses || []);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Historie konnte nicht geladen werden";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    fetchHistory(search);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'positiv': return 'bg-green-500';
      case 'negativ': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  if (!apiKey) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-b from-background to-muted/30">
        <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
          <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between">
            <Link to="/kompakt" className="flex items-center gap-3">
              <img 
                src="/images/logo-klartech.png" 
                alt="Klartech Müritz Logo" 
                className="h-10 w-auto object-contain"
              />
            </Link>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>Nicht angemeldet</CardTitle>
              <CardDescription>Bitte melden Sie sich zuerst an</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/kompakt">
                <Button className="w-full">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Zur Anmeldung
                </Button>
              </Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-background to-muted/30">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between">
          <Link to="/kompakt" className="flex items-center gap-3">
            <img 
              src="/images/logo-klartech.png" 
              alt="Klartech Müritz Logo" 
              className="h-10 w-auto object-contain"
            />
          </Link>
          <nav className="flex items-center gap-4">
            <a 
              href="tel:+4915128958006" 
              className="hidden md:flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Phone className="h-4 w-4" />
              01512 895 800 6
            </a>
            <Link to="/kompakt">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Zurück
              </Button>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <Home className="h-4 w-4" />
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      <main className="flex-1 py-8">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Analyse-Historie</h1>
            <p className="text-muted-foreground">Ihre bisherigen Dokumentenanalysen</p>
          </div>

          {/* Search */}
          <div className="flex gap-2 mb-6">
            <Input
              placeholder="Suche nach Dateiname oder Inhalt..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1"
            />
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />
              Suchen
            </Button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : analyses.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>Noch keine Analysen vorhanden</p>
                <Link to="/kompakt" className="text-primary hover:underline mt-2 inline-block">
                  Erste Analyse starten →
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {analyses.map((analysis) => (
                <Card 
                  key={analysis.id} 
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setSelectedAnalysis(selectedAnalysis?.id === analysis.id ? null : analysis)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <FileText className="h-5 w-5 text-primary" />
                          {analysis.file_name || 'Unbenannt'}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(analysis.created_at)}
                        </CardDescription>
                      </div>
                      {analysis.sentiment?.overall && (
                        <Badge className={getSentimentColor(analysis.sentiment.overall)}>
                          {analysis.sentiment.overall}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  
                  {selectedAnalysis?.id === analysis.id && (
                    <CardContent className="pt-0">
                      <div className="border-t pt-4 mt-2 space-y-4">
                        <div>
                          <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-2">Zusammenfassung</h4>
                          <p className="text-sm">{analysis.summary}</p>
                        </div>
                        {analysis.keywords && analysis.keywords.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-2">Keywords</h4>
                            <div className="flex flex-wrap gap-1">
                              {analysis.keywords.map((kw, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">{kw}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Export Buttons */}
                        <div className="flex gap-2 pt-4 border-t">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              exportAnalysisToPDF({
                                fileName: analysis.file_name,
                                summary: analysis.summary,
                                keywords: analysis.keywords,
                                sentiment: analysis.sentiment,
                                themes: analysis.themes,
                                language: analysis.language,
                                createdAt: analysis.created_at
                              });
                              toast.success("PDF heruntergeladen");
                            }}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            PDF
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(formatAnalysisAsText({
                                fileName: analysis.file_name,
                                summary: analysis.summary,
                                keywords: analysis.keywords,
                                sentiment: analysis.sentiment,
                                themes: analysis.themes,
                                language: analysis.language,
                                createdAt: analysis.created_at
                              }));
                              toast.success("In Zwischenablage kopiert");
                            }}
                          >
                            <Copy className="h-4 w-4 mr-1" />
                            Kopieren
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-border bg-muted/50 py-6">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} Klartech Müritz</p>
        </div>
      </footer>
    </div>
  );
}
